/**
 * Toolbar controls for the flow designer's "Suggest changes" mode.
 *
 * Instead of a floating banner, suggest mode lives in the canvas toolbar: an
 * icon toggle to enter, and — once active — an accent indicator on the left plus
 * Discard / Submit actions on the right (the toolbar itself takes on a primary
 * accent to signal the mode). See `FlowDesignerCanvas` for placement.
 *
 * @module FlowDesigner/proposals/SuggestModeControls
 */

import React from 'react';
import { GitBranch, PaperPlaneTilt, X } from '@phosphor-icons/react';
import {
  Button,
  Flex,
  Text,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TextField,
  TextArea,
} from '@chatsdet/ui';

/** Icon button that enters suggest mode. Lives in the left toolbar group. */
export function SuggestModeToggle({
  canEnter,
  onEnter,
}: {
  canEnter: boolean;
  onEnter: () => void;
}) {
  if (!canEnter) return null;
  return (
    <Button
      variant="icon"
      size="sm"
      iconOnly
      tooltip="Suggest changes"
      onPress={onEnter}
      aria-label="Suggest changes"
    >
      <GitBranch size={16} />
    </Button>
  );
}

/** Accent chip shown in the left toolbar group while suggesting. */
export function SuggestModeIndicator() {
  return (
    <Flex
      align="center"
      gap="xs"
      className="rounded-full border border-primary/20 bg-primary/10 py-1 pl-2 pr-2.5 text-primary"
    >
      <GitBranch size={14} weight="bold" />
      <Text size="sm" weight="semibold" className="whitespace-nowrap">
        Suggesting changes
      </Text>
    </Flex>
  );
}

/** Discard + Submit actions shown in the right toolbar group while suggesting. */
export function SuggestModeActions({
  isSubmitting,
  onDiscard,
  onSubmit,
}: {
  isSubmitting: boolean;
  onDiscard: () => void;
  onSubmit: (params: { title: string; description?: string }) => void | Promise<void>;
}) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');

  React.useEffect(() => {
    if (dialogOpen) {
      setTitle('');
      setDescription('');
    }
  }, [dialogOpen]);

  const canSubmit = title.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({ title: title.trim(), description: description.trim() || undefined });
    setDialogOpen(false);
  };

  return (
    <Flex align="center" gap="sm">
      <Button
        variant="ghost"
        size="sm"
        iconLeft={<X size={14} />}
        onPress={onDiscard}
        isDisabled={isSubmitting}
      >
        Discard
      </Button>
      <DialogTrigger isOpen={dialogOpen} onOpenChange={setDialogOpen}>
        <Button
          variant="primary"
          size="sm"
          iconLeft={<PaperPlaneTilt size={14} />}
          isDisabled={isSubmitting}
          className="min-w-[140px]"
        >
          Submit changes
        </Button>
        <Dialog title="Submit changes for review">
          <DialogContent>
            <Flex direction="column" gap="md">
              <Text size="sm" className="text-muted-foreground">
                Your edits become a proposal others can review and accept. The live flow
                isn&apos;t changed.
              </Text>
              <TextField
                label="Title"
                placeholder="e.g., Add assertion after login"
                value={title}
                onChange={setTitle}
                isDisabled={isSubmitting}
                autoFocus
              />
              <TextArea
                label="Description (optional)"
                placeholder="What does this change do and why?"
                value={description}
                onChange={setDescription}
                isDisabled={isSubmitting}
              />
            </Flex>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setDialogOpen(false)}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button variant="primary" onPress={handleSubmit} isDisabled={!canSubmit}>
              Submit proposal
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Flex>
  );
}
